/**
 * Offline Database - IndexedDB with Dexie.js
 *
 * Provides type-safe IndexedDB storage for all entities.
 * Uses Dexie.js for a clean, promise-based API.
 *
 * Database Structure:
 * - Entity tables: Store cached server data with offline metadata
 * - Sync tables: Track pending operations and sync state
 * - ID mappings: Map temporary IDs to server IDs after sync
 */

import Dexie, { type Table } from 'dexie';

// Import entity types
import type { Transaction, TransactionCategory } from '#/entity';
import type { Contact } from '@/types/contacts.types';
import type { AccountPayable } from '@/types/payables.types';
import type { AccountReceivable } from '@/types/receivables.types';
import type {
  Branch,
  InventoryItem,
  Employee,
  CompositeItem,
} from '#/entity';

// Import offline types
import type {
  OfflineEntity,
  SyncQueueItem,
  SyncMetadata,
  IdMapping,
} from '../types/offline.types';

// ============================================
// DATABASE CLASS
// ============================================

/**
 * Offline Database
 * Extends Dexie for type-safe IndexedDB access
 */
export class OfflineDatabase extends Dexie {
  // Entity tables (cached server data)
  transactions!: Table<OfflineEntity<Transaction>, string>;
  contacts!: Table<OfflineEntity<Contact>, string>;
  payables!: Table<OfflineEntity<AccountPayable>, string>;
  receivables!: Table<OfflineEntity<AccountReceivable>, string>;
  inventory!: Table<OfflineEntity<InventoryItem>, string>;
  employees!: Table<OfflineEntity<Employee>, string>;
  branches!: Table<OfflineEntity<Branch>, string>;
  transactionCategories!: Table<OfflineEntity<TransactionCategory>, string>;
  compositeItems!: Table<OfflineEntity<CompositeItem>, string>;

  // Sync management tables
  syncQueue!: Table<SyncQueueItem, string>;
  syncMetadata!: Table<SyncMetadata, string>;
  idMappings!: Table<IdMapping, string>;

  constructor() {
    super('turath-almandi-offline');

    // Define database schema
    this.version(1).stores({
      // Entity tables with indexes
      // Primary key is '&id' (unique), compound indexes use [field1+field2]
      transactions: '&id, branchId, type, categoryId, date, createdAt, [branchId+date], [type+date], _offlineSynced',
      contacts: '&id, branchId, type, name, isDeleted, [branchId+type], _offlineSynced',
      payables: '&id, branchId, contactId, status, dueDate, [branchId+status], _offlineSynced',
      receivables: '&id, branchId, contactId, status, dueDate, [branchId+status], _offlineSynced',
      inventory: '&id, branchId, name, isActive, [branchId+isActive], _offlineSynced',
      employees: '&id, branchId, status, name, [branchId+status], _offlineSynced',
      branches: '&id, name, isDeleted, _offlineSynced',
      transactionCategories: '&id, type, isDeleted, _offlineSynced',
      compositeItems: '&id, branchId, name, [branchId+name], _offlineSynced',

      // Sync management tables
      syncQueue: '&id, entityType, status, createdAt, [entityType+status], [status+createdAt]',
      syncMetadata: '&entityType, lastSyncedAt, status',
      idMappings: '&tempId, serverId, entityType, createdAt',
    });
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Clear all data from all tables
   * Use with caution - typically only for logout
   */
  async clearAllData(): Promise<void> {
    await this.transaction('rw', this.tables, async () => {
      await Promise.all(this.tables.map((table) => table.clear()));
    });
  }

  /**
   * Clear sync-related tables only
   * Keeps cached data but resets sync state
   */
  async clearSyncData(): Promise<void> {
    await this.transaction('rw', [this.syncQueue, this.syncMetadata, this.idMappings], async () => {
      await this.syncQueue.clear();
      await this.syncMetadata.clear();
      await this.idMappings.clear();
    });
  }

  /**
   * Get pending sync items count
   */
  async getPendingCount(): Promise<number> {
    return this.syncQueue.where('status').equals('pending').count();
  }

  /**
   * Get failed sync items count
   */
  async getFailedCount(): Promise<number> {
    return this.syncQueue.where('status').equals('failed').count();
  }

  /**
   * Get all pending items for a specific entity type
   */
  async getPendingByEntity(entityType: string): Promise<SyncQueueItem[]> {
    return this.syncQueue
      .where('[entityType+status]')
      .equals([entityType, 'pending'])
      .toArray();
  }

  /**
   * Get sync metadata for an entity type
   */
  async getSyncMetadata(entityType: string): Promise<SyncMetadata | undefined> {
    return this.syncMetadata.get(entityType);
  }

  /**
   * Update sync metadata for an entity type
   */
  async updateSyncMetadata(entityType: string, updates: Partial<SyncMetadata>): Promise<void> {
    const existing = await this.syncMetadata.get(entityType);
    if (existing) {
      await this.syncMetadata.update(entityType, updates);
    } else {
      await this.syncMetadata.add({
        entityType: entityType as SyncMetadata['entityType'],
        lastSyncedAt: null,
        syncVersion: 0,
        status: 'idle',
        ...updates,
      });
    }
  }

  /**
   * Get server ID from temporary ID
   */
  async getServerId(tempId: string): Promise<string | undefined> {
    const mapping = await this.idMappings.get(tempId);
    return mapping?.serverId;
  }

  /**
   * Add ID mapping after successful sync
   */
  async addIdMapping(tempId: string, serverId: string, entityType: string): Promise<void> {
    await this.idMappings.add({
      tempId,
      serverId,
      entityType: entityType as IdMapping['entityType'],
      createdAt: Date.now(),
    });
  }

  /**
   * Clean up old ID mappings (older than 24 hours)
   */
  async cleanupOldMappings(): Promise<number> {
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    return this.idMappings.where('createdAt').below(oneDayAgo).delete();
  }

  /**
   * Get database storage usage estimate
   */
  async getStorageEstimate(): Promise<{ usage: number; quota: number } | null> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage ?? 0,
        quota: estimate.quota ?? 0,
      };
    }
    return null;
  }
}

// ============================================
// SINGLETON INSTANCE
// ============================================

/**
 * Singleton database instance
 * Use this throughout the app for consistency
 */
export const offlineDb = new OfflineDatabase();

// ============================================
// INITIALIZATION
// ============================================

/**
 * Initialize the offline database
 * Call this during app startup
 */
export async function initializeOfflineDatabase(): Promise<void> {
  try {
    // Open the database (creates it if it doesn't exist)
    await offlineDb.open();
    console.log('[OfflineDB] Database initialized successfully');

    // Clean up old ID mappings
    const cleaned = await offlineDb.cleanupOldMappings();
    if (cleaned > 0) {
      console.log(`[OfflineDB] Cleaned up ${cleaned} old ID mappings`);
    }
  } catch (error) {
    console.error('[OfflineDB] Failed to initialize database:', error);
    throw error;
  }
}

/**
 * Close the database connection
 * Call this during app cleanup
 */
export function closeOfflineDatabase(): void {
  offlineDb.close();
  console.log('[OfflineDB] Database connection closed');
}

// ============================================
// TYPE EXPORTS
// ============================================

export type { OfflineDatabase };
